
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { Settings2, Trash2, Globe, FileKey, AlertCircle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import AddInstallationDialog from './AddInstallationDialog';
import InstallationKeyModal from './InstallationKeyModal';

interface InstallationsSectionProps {
  userId: string;
}

const InstallationsSection = ({ userId }: InstallationsSectionProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [installationToDelete, setInstallationToDelete] = React.useState<string | null>(null);
  const [selectedInstallation, setSelectedInstallation] = React.useState<{ key: string; domain: string } | null>(null);
  
  const { data: subscriptionData, isLoading: isLoadingSubscription, error: subscriptionError } = useQuery({
    queryKey: ['subscription', userId],
    queryFn: async () => {
      console.log('Fetching active subscription for installations section:', userId);
      
      const { data: subscriptions, error } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          subscription_plan:subscription_plan_id (
            name,
            description,
            price_monthly,
            price_yearly,
            currency
          )
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }
      
      console.log('Found active subscription:', subscriptions);
      return subscriptions;
    },
  });

  const { data: installations, isLoading: isLoadingInstallations, error: installationsError, refetch } = useQuery({
    queryKey: ['installations', userId],
    queryFn: async () => {
      console.log('Fetching installations for user:', userId);
      
      const { data, error } = await supabase
        .from('website_installations')
        .select('*')
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error fetching installations:', error);
        throw error;
      }
      
      console.log('Installations data:', data);
      return data;
    },
    enabled: !!userId,
  });

  const handleDeleteInstallation = async () => {
    if (!installationToDelete) return;

    try {
      const { error } = await supabase
        .from('website_installations')
        .delete()
        .eq('id', installationToDelete);

      if (error) throw error;

      toast({
        title: "Installation deleted",
        description: "The website installation has been removed successfully.",
      });

      queryClient.invalidateQueries({
        queryKey: ['installations', userId],
      });
    } catch (error) {
      console.error('Error deleting installation:', error);
      toast({
        title: "Error",
        description: "Failed to delete installation. Please try again.",
        variant: "destructive",
      });
    }

    setInstallationToDelete(null);
  };

  const isLoading = isLoadingSubscription || isLoadingInstallations;
  const error = subscriptionError || installationsError;

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Website Installations
          </CardTitle>
          <CardDescription>Manage your website installations and settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-destructive mb-4">
            <AlertCircle className="h-5 w-5" />
            <p>Error loading installation data. Please try again later.</p>
          </div>
          <Button onClick={() => refetch()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Website Installations
          </CardTitle>
          <CardDescription>Manage your website installations and settings</CardDescription>
        </div>
        {subscriptionData && <AddInstallationDialog subscriptionId={subscriptionData.id} />}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-full"></div>
              <div className="h-16 bg-muted rounded w-full"></div>
              <div className="h-16 bg-muted rounded w-full"></div>
            </div>
          </div>
        ) : installations && installations.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {installations.map((installation) => (
                  <TableRow key={installation.id}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {installation.domain}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        installation.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {installation.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedInstallation({
                          key: installation.installation_key,
                          domain: installation.domain
                        })}
                      >
                        <FileKey className="h-4 w-4 mr-2" />
                        View Key
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => setInstallationToDelete(installation.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <InstallationKeyModal 
              isOpen={!!selectedInstallation}
              onClose={() => setSelectedInstallation(null)}
              installationKey={selectedInstallation?.key}
              domain={selectedInstallation?.domain || ''}
            />
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-4">
              {subscriptionData 
                ? "No installations yet. Add your first website installation." 
                : "You need an active subscription to add website installations."}
            </p>
            {subscriptionData ? (
              <AddInstallationDialog subscriptionId={subscriptionData.id} />
            ) : (
              <Button onClick={() => window.location.href = '/pricing'}>
                Add Installation
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!installationToDelete} onOpenChange={() => setInstallationToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              installation and remove access to the widget for this domain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInstallation}>
              Delete Installation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default InstallationsSection;
